import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Zap, MessageSquareText, Sparkles, Check, Shield, BarChart3, Menu, X, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { MetaVerifiedBadge } from "../components/MetaVerifiedBadge";
import CommunityPartners from "../components/CommunityPartners";
import LandingFAQ from "../components/LandingFAQ";
import UseCases from "../components/UseCases";

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {});
  }, []);

  // Close drawer if user resizes up to desktop (avoids stale open state)
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMobileNavOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lock body scroll while drawer open
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  const closeNav = () => setMobileNavOpen(false);

  return (
    <div className="min-h-screen bg-bone text-white">
      {/* Nav */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-bone/80 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 h-16 sm:h-20 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="landing-logo">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden bg-obsidian shrink-0 flex items-center justify-center">
              <img src="/instavibe-logo.jpg" alt="InstaVibe" className="w-full h-full object-cover scale-[1.35]" />
            </div>
            <div className="font-heading font-black text-xl sm:text-2xl tracking-tighter leading-none truncate">
              InstaVibe<span className="text-klein">.</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#features" className="hover:text-klein transition-colors" data-testid="landing-nav-features">Features</a>
            <a href="#use-cases" className="hover:text-klein transition-colors" data-testid="landing-nav-usecases">Use cases</a>
            <a href="#pricing" className="hover:text-klein transition-colors" data-testid="landing-nav-pricing">Pricing</a>
            <a href="#faq" className="hover:text-klein transition-colors" data-testid="landing-nav-faq">FAQ</a>
            <Link to="/blog" className="hover:text-klein transition-colors" data-testid="landing-nav-blog">Blog</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline-flex btn-ghost text-sm" data-testid="landing-login-btn">Log in</Link>
            <Link to="/signup" className="btn-primary text-xs sm:text-sm py-2.5 sm:py-3 px-3 sm:px-4 whitespace-nowrap" data-testid="landing-signup-btn">
              Start free <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden p-2 -mr-2 text-white/80 hover:text-white"
              aria-label="Open menu"
              data-testid="landing-mobile-menu-open"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-[100] md:hidden" data-testid="landing-mobile-drawer">
          <button
            type="button"
            onClick={closeNav}
            aria-label="Close menu"
            className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm"
            data-testid="landing-mobile-drawer-overlay"
          />
          <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-xs bg-bone border-l border-white/10 p-6 flex flex-col gap-1 animate-fade-up">
            <div className="flex items-center justify-between mb-6">
              <div className="font-heading font-black text-xl tracking-tighter">Menu</div>
              <button
                type="button"
                onClick={closeNav}
                aria-label="Close menu"
                className="p-2 -mr-2 text-white/80 hover:text-white"
                data-testid="landing-mobile-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <a href="#features" onClick={closeNav} className="py-3 border-b border-white/5 hover:text-klein" data-testid="landing-mobile-nav-features">Features</a>
            <a href="#use-cases" onClick={closeNav} className="py-3 border-b border-white/5 hover:text-klein" data-testid="landing-mobile-nav-usecases">Use cases</a>
            <a href="#pricing" onClick={closeNav} className="py-3 border-b border-white/5 hover:text-klein" data-testid="landing-mobile-nav-pricing">Pricing</a>
            <a href="#faq" onClick={closeNav} className="py-3 border-b border-white/5 hover:text-klein" data-testid="landing-mobile-nav-faq">FAQ</a>
            <Link to="/blog" onClick={closeNav} className="py-3 border-b border-white/5 hover:text-klein" data-testid="landing-mobile-nav-blog">Blog</Link>
            <Link to="/contact" onClick={closeNav} className="py-3 border-b border-white/5 hover:text-klein" data-testid="landing-mobile-nav-contact">Contact</Link>
            <div className="mt-6 flex flex-col gap-3">
              <Link to="/login" onClick={closeNav} className="btn-secondary w-full justify-center" data-testid="landing-mobile-login">Log in</Link>
              <Link to="/signup" onClick={closeNav} className="btn-primary w-full justify-center" data-testid="landing-mobile-signup">
                Start free <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="pt-28 sm:pt-32 md:pt-40 pb-16 sm:pb-24 md:pb-32 px-4 sm:px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 md:gap-10 items-end">
          <div className="lg:col-span-8 animate-fade-up">
            <div className="overline mb-4 sm:mb-6" data-testid="hero-eyebrow">— Instagram DM Automation · Built for creators</div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl lg:text-8xl leading-[0.95] tracking-tighter" data-testid="hero-title">
              Turn every<br/>
              <span className="italic font-light text-white/90">comment</span> into a<br/>
              <span className="gradient-text">conversion.</span>
            </h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg md:text-xl text-white/70 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              InstaVibe auto-replies to your Instagram DMs the moment a follower sends a keyword — send them a link, a freebie, or a personal note on autopilot.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <Link to="/signup" className="btn-primary w-full sm:w-auto justify-center" data-testid="hero-cta-primary">Start 14-day free trial <ArrowUpRight className="w-4 h-4" /></Link>
              <a href="#how" className="btn-secondary w-full sm:w-auto justify-center" data-testid="hero-cta-secondary">See how it works</a>
            </div>
            <div className="mt-6 sm:mt-8">
              <MetaVerifiedBadge variant="default" />
            </div>
            <div className="mt-8 sm:mt-10 grid grid-cols-3 sm:flex sm:gap-8 gap-4 text-sm">
              <div><div className="font-heading font-black text-xl sm:text-2xl">12K+</div><div className="text-xs sm:text-sm text-white/60">creators</div></div>
              <div><div className="font-heading font-black text-xl sm:text-2xl">2.4M</div><div className="text-xs sm:text-sm text-white/60">DMs sent</div></div>
              <div><div className="font-heading font-black text-xl sm:text-2xl">99.9%</div><div className="text-xs sm:text-sm text-white/60">uptime</div></div>
            </div>
          </div>
          <div className="lg:col-span-4 order-first lg:order-none">
            <div className="relative animate-float max-w-xs sm:max-w-sm mx-auto lg:max-w-none" data-testid="hero-logo-showcase">
              <div className="absolute -inset-6 sm:-inset-8 bg-gradient-to-br from-klein/40 via-klein/20 to-cyan/30 blur-3xl"></div>
              <div className="relative rounded-sm overflow-hidden bg-bone aspect-square shadow-2xl border border-white/10 glow-purple">
                <img src="/hero-dm-it-get-it.jpg" alt="InstaVibe — DM it, get it. Smart DMs. Instant impact." className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder pitch video — between Hero and Features */}
      <section className="px-4 sm:px-6 md:px-12 max-w-[1100px] mx-auto pb-16 sm:pb-24 md:pb-32" data-testid="founder-pitch-section">
        <div className="overline mb-3 sm:mb-4 text-center" data-testid="founder-pitch-eyebrow">— A 60-second pitch</div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl md:text-4xl tracking-tighter text-center mb-8 sm:mb-12 max-w-2xl mx-auto leading-tight" data-testid="founder-pitch-title">
          Watch the <span className="gradient-text">$9.99 Mogul deal</span> in your founder's own words.
        </h2>
        <div className="relative">
          {/* Neon purple→cyan glow that matches brand */}
          <div className="absolute -inset-1.5 sm:-inset-2 md:-inset-4 bg-gradient-to-br from-klein/60 via-klein/30 to-cyan/50 blur-2xl opacity-80 pointer-events-none" aria-hidden="true"></div>
          <div className="relative rounded-sm overflow-hidden border border-white/10 glow-purple bg-obsidian" data-testid="founder-pitch-video-frame">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                src="https://app.heygen.com/embeds/5a54de6137474bfeb88ffc108ddb0ba9?autoplay=1&muted=1"
                title="InstaVibe founder pitch — Mogul $9.99 deal"
                className="absolute inset-0 w-full h-full"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                loading="lazy"
                data-testid="founder-pitch-iframe"
              ></iframe>
            </div>
          </div>
        </div>
        <div className="mt-8 sm:mt-10 flex flex-col items-center gap-3 px-4 sm:px-0">
          <Link to="/signup" className="btn-primary w-full sm:w-auto justify-center" data-testid="founder-pitch-cta">
            Claim the $9.99 Deal <ArrowUpRight className="w-4 h-4" />
          </Link>
          <div className="text-xs text-white/50 text-center">No credit card to start · Cancel anytime</div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 sm:gap-10">
          <div className="lg:col-span-4">
            <div className="overline mb-3 sm:mb-4">— Features</div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-tight">
              Everything you need, nothing you don't.
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4 sm:gap-6">
            <Feature icon={Zap} title="Keyword triggers" text="Match by exact, contains, or starts-with. Stack unlimited rules per account." testid="feature-1" />
            <Feature icon={MessageSquareText} title="Instant replies" text="Sub-second response time. Delivered via Meta's official Graph API." testid="feature-2" />
            <Feature icon={BarChart3} title="Conversion analytics" text="Track triggers fired, DMs sent, and link click-through in one place." testid="feature-3" />
            <Feature icon={Shield} title="Secure OAuth" text="One-click Instagram Business connect. Tokens encrypted at rest." testid="feature-4" />
            <Feature icon={Sparkles} title="Link in DM" text="Slide in a link after the keyword — the new way to drive traffic from IG." testid="feature-5" />
            <Feature icon={Check} title="Zero code setup" text="Paste your keyword, write your reply, flip it live. Takes two minutes." testid="feature-6" />
          </div>
        </div>
      </section>

      {/* Use cases */}
      <UseCases />

      {/* DM to Destination showcase */}
      <section id="showcase" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 bg-obsidian text-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="overline mb-3 sm:mb-4 text-white/60">— See it in action</div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-5 sm:mb-6">
                From DM<br/>to Destination.<br/>
                <span className="italic font-light text-klein">In a Vibe.</span>
              </h2>
              <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 max-w-md">
                A follower types one keyword. We route them to the exact link, product page, or freebie in under a second — with a personal touch.
              </p>
              <div className="space-y-4">
                <ShowcasePoint title="Lightning Fast" text="Instant access. Zero friction." />
                <ShowcasePoint title="Effortless experience" text="Built for speed. Designed for you." />
                <ShowcasePoint title="More conversations" text="Turn DMs into real engagement." />
              </div>
              <Link to="/signup" className="btn-primary mt-8 sm:mt-10 w-full sm:w-auto justify-center" data-testid="showcase-cta">
                Start 14-day free trial <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative" data-testid="showcase-visual">
                <div className="absolute -inset-6 sm:-inset-8 bg-gradient-to-br from-klein/25 via-klein/15 to-cyan/25 blur-3xl"></div>
                <div className="relative rounded-2xl overflow-hidden border border-bone/10 shadow-2xl">
                  <img src="/hero-dm-destination.jpg" alt="InstaVibe — from Instagram DM to destination" className="w-full h-auto block" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 bg-obsidian text-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="overline mb-3 sm:mb-4 text-white/60">— Workflow</div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter mb-10 sm:mb-16 max-w-2xl leading-tight">Three steps to a DM machine.</h2>
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { n: "01", title: "Connect Instagram", text: "Click connect, approve the Meta permissions. We never post or read anything beyond DMs." },
              { n: "02", title: "Define keywords", text: "Set up triggers like 'PAISLEY' or 'recipe'. Add a response text and optional link." },
              { n: "03", title: "Ship and scale", text: "Go live. Watch every matching DM get answered instantly, 24/7." },
            ].map((s) => (
              <div key={s.n} className="border-t border-bone/20 pt-6 sm:pt-8" data-testid={`step-${s.n}`}>
                <div className="font-heading font-black text-4xl sm:text-5xl text-klein mb-3 sm:mb-4">{s.n}</div>
                <div className="font-heading font-bold text-lg sm:text-xl mb-2">{s.title}</div>
                <div className="text-white/70 leading-relaxed text-sm sm:text-base">{s.text}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 sm:mt-12 text-sm text-white/50 flex flex-wrap items-center gap-2">
            <span>Powered by the</span>
            <a
              href="https://developers.facebook.com/docs/instagram-api/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-cyan hover:text-white transition-colors font-semibold"
              data-testid="meta-developers-outbound-link"
            >
              official Meta Instagram Graph API
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <span>· Meta Verified Business Tech Provider.</span>
          </div>
        </div>
      </section>

      {/* Pricing — vertical stack on mobile, side-by-side from md up */}
      <section id="pricing" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="overline mb-3 sm:mb-4">— Pricing</div>
        <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter mb-10 sm:mb-16 max-w-2xl leading-tight">Simple pricing. Cancel anytime.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl">
          {plans.map((p) => (
            <div key={p.id} className={`surface-card p-6 sm:p-8 ${p.popular ? "border-white" : ""}`} data-testid={`pricing-card-${p.id}`}>
              {p.popular && <div className="inline-block bg-klein text-white px-3 py-1 text-[10px] sm:text-xs font-bold tracking-wider uppercase mb-3 sm:mb-4">Most popular</div>}
              <div className="font-heading font-black text-2xl sm:text-3xl">{p.name}</div>
              <div className="my-4 sm:my-6 flex items-baseline gap-2 flex-wrap">
                <span className="font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter">${p.price_monthly}</span>
                <span className="text-white/60 text-sm sm:text-base">/month</span>
              </div>
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-klein shrink-0 mt-0.5" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup" className={`${p.popular ? "btn-primary" : "btn-secondary"} w-full justify-center`} data-testid={`pricing-cta-${p.id}`}>
                Start with {p.name}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <LandingFAQ />

      {/* CTA */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="surface-card bg-klein text-white p-8 sm:p-12 md:p-20 text-center" data-testid="cta-block">
          <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-6xl tracking-tighter mb-4 sm:mb-6 leading-tight">Every missed DM is a missed sale.</h2>
          <p className="text-white/80 text-base sm:text-lg max-w-xl mx-auto mb-8 sm:mb-10">Be the brand that answers in 0.8 seconds, 24 hours a day. Try InstaVibe free for 14 days.</p>
          <Link to="/signup" className="inline-flex items-center justify-center gap-2 bg-surface text-klein px-6 sm:px-8 py-3.5 sm:py-4 font-bold tracking-wide hover:bg-bone transition-colors w-full sm:w-auto" data-testid="cta-signup-btn">
            Start free <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Community Partners — fetched live from paisley.org.uk */}
      <CommunityPartners />

      <footer className="border-t border-white/10 py-8 sm:py-10 px-4 sm:px-6 md:px-12 max-w-[1400px] mx-auto flex flex-wrap gap-4 sm:gap-6 justify-between items-center text-xs sm:text-sm text-white/60">
        <div>© 2026 InstaVibe. Not affiliated with Meta.</div>
        <MetaVerifiedBadge variant="compact" />
        <div className="flex flex-wrap gap-4 sm:gap-6">
          <Link to="/blog" className="hover:text-white" data-testid="footer-blog">Blog</Link>
          <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms</Link>
          <Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact</Link>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, text, testid }) {
  return (
    <div className="surface-card p-6" data-testid={testid}>
      <Icon className="w-6 h-6 text-klein mb-4" strokeWidth={1.5} />
      <div className="font-heading font-bold text-lg mb-2">{title}</div>
      <div className="text-sm text-white/70 leading-relaxed">{text}</div>
    </div>
  );
}

function ChatBubble({ side, text, highlight, link }) {
  const isOut = side === "out";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] px-4 py-2 text-sm ${
        isOut ? "bg-klein text-white" : highlight ? "bg-klein/10 border border-klein/30 text-klein font-bold font-mono" : "bg-surface/5 text-white"
      } ${link ? "font-mono underline" : ""}`}>
        {text}
      </div>
    </div>
  );
}

function ShowcasePoint({ title, text }) {
  return (
    <div className="flex items-start gap-4 pl-6 border-l-2 border-klein/50">
      <div>
        <div className="font-heading font-bold text-base text-white">{title}</div>
        <div className="text-sm text-white/60">{text}</div>
      </div>
    </div>
  );
}
